import { Chip } from "@mui/material";
import { Popover } from "antd";
import { ContextMenuWrapper } from "components/ContextMenu/ContextMenu";
import { CustomLinkMarkdown } from "components/Form/components/Markdown/Markdown";
import { Icon, IconLike } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { RuntimeProgressBar } from "components/RuntimeProgressBar/RuntimeProgressBar";
import { ExecutionStatusBadge } from "components/StatusBadge/ExecutionStatusBadge";
import { getRecordColumnCellStyle } from "components/Table/recordColumnWidths";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import { getExecutionMode } from "config/ts-utils";
import {
    InTrashBadge,
    PendingDeleteBadge,
    PendingRestoreBadge,
    PendingTrashBadge,
} from "containers/Resources/components/Badges/Badges";
import badgeStyle from "containers/Resources/components/Badges/Badges.module.scss";
import InlineActionButtons from "containers/Resources/components/CommonActions/InlineActionButtons";
import {
    EdgeKind,
    edgeKindColors,
    edgeKindLabels,
} from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/dependencyVisualization.service";
import { getRelativeUrl } from "utils/service";
import { statusColors } from "utils/utils";
import { resourceByName } from "./resources";
import { createdAtDto, createdByDto, modifiedAtDto, modifiedByDto } from "./utils";
const executionTypeIconMapping = {
    flow: IconCollection.codeTags,
    script: IconCollection.codeTags,
    connection: IconCollection.connection,
    schedule: IconCollection.timetable,
    wrapper: IconCollection.codeBraces,
};

type IconMapping = {
    [key: string]: IconLike;
};

export const connectorTypeIconMapping: IconMapping = {
    aws: IconCollection.aws,
    git: IconCollection.git,
    google: IconCollection.google,
    imap: IconCollection.emailReceiveOutline,
    k8s: IconCollection.kubernetes,
    ldap: IconCollection.accountGroup,
    mysql: IconCollection.database,
    ps: IconCollection.powershell,
    redis: IconCollection.memory,
    rest: IconCollection.web,
    scp: IconCollection.contentCopy,
    smb: IconCollection.contentCopy,
    smtp: IconCollection.emailSendOutline,
    soap: IconCollection.handWashOutline,
    sqlmssql: IconCollection.database,
    sqloracle: IconCollection.database,
    sqlpg: IconCollection.database,
    ssh: IconCollection.consoleLine,
    vault: IconCollection.safe,
    xmlrpc: IconCollection.xml,
};

const ENDED_STATUSES = new Set(["ENDED_SUCCESS", "ENDED_ERROR", "ENDED_CANCELLED"]);

const getRuntimeInfo = (execution: any) => {
    const { id, status, started_at } = execution;
    const isInFlight = started_at && !ENDED_STATUSES.has(status);
    if (!isInFlight) return null;
    return (
        <div className="info-cell-row">
            <div className="additionalInfo" style={{ width: "100%" }}>
                <div>
                    {status === "AT_ENGINE" && (
                        <>running since <TimeAgo datetime={started_at} /></>
                    )}
                    {status !== "AT_ENGINE" && status}
                </div>
                <RuntimeProgressBar executionId={id} startedAt={started_at} compact />
            </div>
        </div>
    );
};

const getDependencyWaitingInfo = (execution: any) => {
    const { dependency_id, dependency_id_dependency } = execution;
    if (!dependency_id) return null;
    const dependencyCount =
        dependency_id_dependency?.dependency_execution_list_dependency_id
            ?.dependency_execution_count;
    return (
        <div className="info-cell-row">
            <div className="additionalInfo">
                waits for {dependencyCount} dependenc{dependencyCount === 1 ? "y" : "ies"}
            </div>
        </div>
    );
};

const getLockWaitingInfo = (execution: any) => {
    const { setting_id, setting_id_setting } = execution;
    if (!setting_id) return null;
    return (
        <div className="info-cell-row">
            <div className="additionalInfo">
                waits for lock
                <RecordLabel
                    link={getRelativeUrl("setting", setting_id)}
                    name={setting_id_setting?.name}
                    icon={resourceByName.setting.icon}
                />
            </div>
        </div>
    );
};

const getResponseWaitingInfo = (execution: any) => {
    const { message_id, message_id_message } = execution;
    if (!message_id) return null;
    return (
        <div className="info-cell-row">
            <div className="additionalInfo">
                waits for message
                <RecordLabel
                    link={getRelativeUrl("message", message_id)}
                    name={message_id_message?.subject}
                    icon={resourceByName.message.icon}
                />
            </div>
        </div>
    );
};

export const getScheduledForInfo = (execution: any, setNewValue?: any) => {
    const { scheduled_for, setting_id, dependency_id, message_id, status } = execution;
    if (!scheduled_for || status === "PAUSED") return null;

    const getChildren = () => (
        <div className="info-cell-row">
            <div className="additionalInfo">
                {setting_id || dependency_id || message_id ? "timeout" : "runs"}{" "}
                <TimeAgo datetime={scheduled_for} />
            </div>
        </div>
    );

    if (setNewValue) {
        return (
            <ContextMenuWrapper
                handleClick={() =>
                    setNewValue({
                        field: "scheduled_for",
                        value: scheduled_for,
                    })
                }
            >
                {getChildren()}
            </ContextMenuWrapper>
        );
    } else {
        return getChildren();
    }
};

const getFlowInfo = (execution: any) => {
    const { flow_id, flow_id_flow } = execution;
    if (flow_id) {
        return (
            <div className="info-cell-row">
                <RecordLabel
                    link={getRelativeUrl("flow", flow_id)}
                    name={flow_id_flow?.name}
                    icon={executionTypeIconMapping.flow}
                />
            </div>
        );
    }
    return (
        <div className="info-cell-row">
            <Icon icon={executionTypeIconMapping.flow} /> flow
        </div>
    );
};

const getConnectionInfo = (execution: any) => {
    const { connector_id, connector_id_connector, connector_type } = execution;
    // TODO (low) refactor once dependency is typesafe
    const connectorType: keyof typeof connectorTypeIconMapping = connector_type.toLowerCase();
    const connectorIcon = connectorTypeIconMapping[connectorType];
    if (connector_id) {
        return (
            <>
                <div className="info-cell-row">
                    <Icon icon={connectorIcon} /> {connector_type.toLowerCase()} connection to
                </div>
                <div className="info-cell-row">
                    <RecordLabel
                        link={getRelativeUrl("connector", connector_id)}
                        name={connector_id_connector?.name}
                        icon={resourceByName.connector.icon}
                    />
                </div>
            </>
        );
    }
    return (
        <div className="info-cell-row">
            <Icon icon={connectorIcon} /> {connector_type.toLowerCase()} connection
        </div>
    );
};

const getScheduleInfo = (execution: any) => {
    const { schedule_id, schedule_id_schedule } = execution;
    if (schedule_id) {
        return (
            <div className="info-cell-row">
                <RecordLabel
                    link={getRelativeUrl("schedule", schedule_id)}
                    name={schedule_id_schedule?.name}
                    icon={executionTypeIconMapping.schedule}
                />
            </div>
        );
    }
    return (
        <div className="info-cell-row">
            <Icon icon={executionTypeIconMapping.schedule} /> schedule
        </div>
    );
};

const getWrapperInfo = (execution: any) => {
    const { wrapper_id, wrapper_id_wrapper } = execution;
    if (wrapper_id) {
        return (
            <div className="info-cell-row">
                <RecordLabel
                    link={getRelativeUrl("wrapper", wrapper_id)}
                    name={wrapper_id_wrapper?.name}
                    icon={executionTypeIconMapping.wrapper}
                />
            </div>
        );
    }
    return (
        <div className="info-cell-row">
            <Icon icon={executionTypeIconMapping.wrapper} /> wrapper
        </div>
    );
};

const getScriptInfo = (execution: any) => {
    return (
        <div className="info-cell-row">
            <Icon icon={executionTypeIconMapping.script} /> script
        </div>
    );
};

const getIsRemoteProcessedInfo = (execution: any) => {
    if (!execution?.sync_config_id) return null;
    const sync_config_link = (
        <RecordLabel
            link={getRelativeUrl("sync_config", execution?.sync_config_id)}
            name={execution?.sync_config_id_identity?.name}
            icon={resourceByName.sync_config.icon}
        />
    );
    if (execution?.is_remote_processed) {
        return <>Running remotely in {sync_config_link}</>;
    }
    return <>Remotely started by {sync_config_link}</>;
};

const getLineNumberInfo = (execution: any) => {
    if (!execution?.lineno) return null;
    return (
        <>
            Line {execution.lineno} {execution.last_command}
        </>
    );
};

const cellStyle = { style: { width: "260px", maxWidth: "260px" } };

export const executionInfoColumn = (setNewValue) => ({
    label: "Info",
    key: "info",
    dto: (data: any) => {
        const { type } = data;
        const runtimeInfo = getRuntimeInfo(data);
        const lockWaitingInfo = getLockWaitingInfo(data);
        const responseWaitingInfo = getResponseWaitingInfo(data);
        const dependencyWaitingInfo = getDependencyWaitingInfo(data);
        const scheduledForInfo = getScheduledForInfo(data);
        const isRemoteProcessedInfo = getIsRemoteProcessedInfo(data);
        const lineNumberInfo = getLineNumberInfo(data);
        let typeInfo;
        switch (type) {
            case "FLOW":
                typeInfo = getFlowInfo(data);
                break;
            case "CONNECTION":
                typeInfo = getConnectionInfo(data);
                break;
            case "SCHEDULE":
                typeInfo = getScheduleInfo(data);
                break;
            case "WRAPPER":
                typeInfo = getWrapperInfo(data);
                break;
            case "SCRIPT":
                typeInfo = getScriptInfo(data);
                break;
            default:
                typeInfo = type;
                break;
        }
        return (
            <ContextMenuWrapper handleClick={() => setNewValue({ field: "type", value: type })}>
                <div className="info-cell">
                    {typeInfo}
                    {runtimeInfo}
                    {lockWaitingInfo}
                    {responseWaitingInfo}
                    {dependencyWaitingInfo}
                    {scheduledForInfo}
                    {isRemoteProcessedInfo}
                    {lineNumberInfo}
                </div>
            </ContextMenuWrapper>
        );
    },
    shown: true,
    sortable: false,
    cellStyle,
});

/**
 * Children-tab "Relation" column: a colour-coded pill showing how each descendant
 * relates to the current execution — child / dependency / both — reusing the SAME palette
 * as the dependency-visualization edges + legend. `kindMap` (execution id → EdgeKind) is
 * derived from the graph's already-loaded, backend-tagged edges (buildExecutionKindMap).
 * Rows without a tagged relation (e.g. beyond the graph's node cap) render "—".
 */
const relationColumn = (kindMap: Record<string, EdgeKind>) => ({
    label: "Relation",
    key: "relation",
    dto: (data: any) => {
        const kind: EdgeKind | undefined = kindMap[data?.id];
        if (kind == null) {
            return <span className="relationCellEmpty">—</span>;
        }
        return (
            <Chip
                label={edgeKindLabels[kind]}
                size="small"
                variant="outlined"
                sx={{ color: edgeKindColors[kind], borderColor: edgeKindColors[kind] }}
            />
        );
    },
    shown: true,
    sortable: false,
});

export const executionsListingColumns = (
    setNewValue,
    kindMap?: Record<string, EdgeKind>
) => [
    {
        label: "Name",
        key: "name",
        dto: (data: any, refreshAction: () => void) => {
            const {
                id,
                name,
                deleted_at,
                pending_trash_identity_id,
                pending_restore_identity_id,
                pending_delete_identity_id,
            } = data;
            return (
                <ContextMenuWrapper handleClick={() => setNewValue({ field: "name", value: name })}>
                    <div className="nameCell">
                        <RecordLabel
                            link={getRelativeUrl("execution", id)}
                            name={name}
                            icon={resourceByName.execution.icon}
                        />
                        <InlineActionButtons
                            resourceData={data}
                            afterButtonAction={refreshAction}
                            hideGoToParent
                        >
                            <div
                                className={`${badgeStyle.stateChips} ${badgeStyle.stateChipsWrap}`}
                            >
                                {getExecutionMode(data) ? (
                                    <Chip
                                        label={getExecutionMode(data)}
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                    />
                                ) : null}
                                <InTrashBadge deletedAt={deleted_at} />
                                <PendingTrashBadge
                                    pendingTrashIdentityId={pending_trash_identity_id}
                                />
                                <PendingRestoreBadge
                                    pendingRestoreIdentityId={pending_restore_identity_id}
                                />
                                <PendingDeleteBadge
                                    pendingDeleteIdentityId={pending_delete_identity_id}
                                />
                            </div>
                        </InlineActionButtons>
                    </div>
                </ContextMenuWrapper>
            );
        },
        shown: true,
        sortable: false,
        // Floor-only Name column (grow to the full execution name instead of a
        // fixed 260px clip), matching every other list view.
        // (operator-task-fe-list-view-name-column-width-cap-vs-clip)
        cellStyle: getRecordColumnCellStyle("name"),
    },
    executionInfoColumn(setNewValue),
    {
        label: "Status",
        key: "status",
        dto: (data: any) => {
            const message = data?.message;
            // TODO (low) refactor once <data> is typesafe
            const status: keyof typeof statusColors = data?.status;

            const badge = <ExecutionStatusBadge data={data} />;
            return (
                <ContextMenuWrapper
                    handleClick={() => setNewValue({ field: "status", value: status })}
                >
                    <div className="statusCell">
                        {message ? (
                            <Popover
                                content={
                                    <CustomLinkMarkdown
                                        markdownString={message}
                                        className="statusMessageMarkdown"
                                    />
                                }
                                trigger="hover"
                                placement="topLeft"
                                overlayClassName="statusCellPopover"
                            >
                                {badge}
                                <div className="statusMessage">{String(message).trim()}</div>
                            </Popover>
                        ) : (
                            <>{badge}</>
                        )}
                    </div>
                </ContextMenuWrapper>
            );
        },
        shown: true,
        sortable: true,
    },
    ...(kindMap ? [relationColumn(kindMap)] : []),
    {
        label: "Created by",
        key: "created_by",
        dto: (data: any) => createdByDto(data, setNewValue),
        shown: true,
    },
    {
        label: "Created at",
        key: "created_at",
        dto: (data: any) => createdAtDto(data, setNewValue),
        shown: true,
        sortable: true,
    },
    {
        label: "Modified by",
        key: "modified_by",
        dto: (data: any) => modifiedByDto(data, setNewValue),
        shown: true,
    },
    {
        label: "Modified at",
        key: "modified_at",
        dto: (data: any) => modifiedAtDto(data, setNewValue),
        shown: true,
        sortable: true,
    },
    {
        label: "Scheduled for",
        key: "scheduled_for",
        dto: (data: any) => getScheduledForInfo(data, setNewValue),
        shown: false,
        sortable: true,
    },
    {
        label: "Priority",
        key: "priority",
        dto: (data: any) => data.priority,
        shown: false,
        sortable: true,
    },
];

export const executionsActions = {
    shown: ["pause/resume", "cancel"],
    hidden: ["delete"],
};

// TODO (low) check whether this is used anywhere
export const executionsDetailsSidebar = [
    {
        label: "ID",
        valueKey: "id",
        dto: (value: any) => value,
    },
    {
        label: "Last Command",
        valueKey: "last_command",
        dto: (value: any) => value,
    },
    {
        label: "Parent Execution Link",
        valueKey: "",
        dto: (value: any) => value,
    },
    {
        label: "Child Execution Search",
        valueKey: "",
        dto: (value: any) => value,
    },
    {
        label: "Ended At",
        valueKey: "ended_at",
        dto: (value: any) => value,
    },
    {
        label: "Runtime",
        valueKey: "runtime",
        dto: (value: any) => value,
    },
    {
        label: "Link To Flow",
        valueKey: "flow_id",
        dto: (value: any) => value,
    },
    {
        label: "Link To Location",
        valueKey: "",
        dto: (value: any) => value,
    },
];
